import React, { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import JSZip from 'jszip';
import moment from 'moment';
import { formatCurrency2SigFig } from '../../../../utils/campaigns';

// =============================================================================
// BasketItemsTabController
//
// The date range is no longer local state (#11758). The loader resolves it —
// URL params, else the campaign window — and hands it down, so the picker, the
// KPI series, the items table, the orders rail and the export all read one
// value. The old copy in `useState` was never passed to the view, so the
// picker fell back to its own default label and displayed a range that had
// nothing to do with what was being filtered.
// =============================================================================

const BasketItemsTabController = ({
  campaignOrder,
  pageSize,
  basketItemsData,
  basketItemsGroupedOrderId,
  basketItemsGroupedTotalCount,
  currentPage,
  basketItemsDataLoading,
  basketItemsByDate,
  dateRange,
  defaultDateRange,
  dateBounds,
  setExportButtonMenuItems,
  setExportButtonLoading,
  setExportButtonDisable,
  ...props
}) => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [expandedRowKeys, setExpandedRowKeys] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState(queryParams.get('searchTerm') || '');
  const [basketItemsSearchTerms, setBasketItemsSearchTerms] = React.useState([]);

  // Filter the KPI series on the exact instants the server filtered the items
  // with, rather than a local-zone `isSame…(…, 'day')` comparison. Same
  // boundaries, same population — that is the whole point of #11758.
  const filteredBasketItemsByDate =
    basketItemsByDate && dateBounds
      ? basketItemsByDate.filter((item) => {
          const itemDate = moment(item.date);
          return itemDate.isSameOrAfter(dateBounds.gte) && itemDate.isBefore(dateBounds.lt);
        })
      : basketItemsByDate || [];

  // Consolidate basket items by name
  const consolidatedBasketItems = basketItemsData
    ? Object.values(
        basketItemsData.reduce((acc, item) => {
          const key = item.name;
          if (!acc[key]) {
            acc[key] = {
              id: item.id,
              name: item.name,
              quantity: 0,
              price: item.price,
              total: 0,
            };
          }
          acc[key].quantity += item.quantity;
          acc[key].total += item.total;
          return acc;
        }, {})
      )
    : [];

  // Filter basket items based on multiple search terms (tags) - OR logic
  const matchesSearchTerms = (name, terms) => {
    if (!terms || terms.length === 0) return true;
    const itemName = (name || '').toLowerCase();
    return terms.some((term) => itemName.includes(term.toLowerCase()));
  };

  const filteredBasketItems = consolidatedBasketItems.filter((item) =>
    matchesSearchTerms(item.name, basketItemsSearchTerms)
  );

  // Aggregate summary values from filtered basketItemsByDate
  const detailBarSummary =
    filteredBasketItemsByDate.length > 0
      ? filteredBasketItemsByDate.reduce(
          (acc, item) => {
            acc.transactions += item.transactions || 0;
            acc.revenue += item.revenue || 0;
            acc.quantity += item.quantity || 0;
            return acc;
          },
          { transactions: 0, revenue: 0, quantity: 0 }
        )
      : { transactions: 0, revenue: 0, quantity: 0 };

  // The export menu is registered once on mount, so everything it reads has to
  // come through a ref or it would close over the first render's values.
  const exportStateRef = useRef({});
  useEffect(() => {
    exportStateRef.current = { basketItemsData, basketItemsSearchTerms, dateRange };
  }, [basketItemsData, basketItemsSearchTerms, dateRange]);

  // Set up export button only once on mount
  useEffect(() => {
    setExportButtonDisable(false);
    setExportButtonMenuItems([
      {
        key: 'basketItems',
        text: 'Basket Items',
        confirmTitle: 'Please allow Popup',
        confirmDescription: 'Popup should be allowed on your browser to download this report',
        hasConfirm: true,
        onClick: () => {
          setExportButtonLoading(true);

          const {
            basketItemsData: rows = [],
            basketItemsSearchTerms: terms = [],
            dateRange: range,
          } = exportStateRef.current;

          // The range is stamped into the filename and every row carries its
          // own date, so a client can verify the export against the screen
          // instead of filing a ticket (#11758). Rows are the underlying
          // records rather than the table's name-consolidated view — finer
          // grained, same totals, and the only form that can carry a date.
          const rangeLabel =
            range && range[0] && range[1]
              ? `${range[0].format('YYYY-MM-DD')} to ${range[1].format('YYYY-MM-DD')}`
              : 'all dates';

          const zip = new JSZip();
          const folder = zip.folder('Basket Items');

          folder.file(
            `${campaignOrder.name} Basket Items ${rangeLabel}.csv`,
            Papa.unparse(
              (rows || [])
                .filter((b) => matchesSearchTerms(b.name, terms))
                .map((b) => ({
                  Name: b.name,
                  Date: b.date ? moment(b.date).format('YYYY-MM-DD') : '',
                  Quantity: b.quantity,
                  Price: formatCurrency2SigFig(b.price),
                  Total: formatCurrency2SigFig(b.total),
                }))
            )
          );
          zip.generateAsync({ type: 'blob' }).then(
            (blob) => {
              saveAs(
                blob,
                `${campaignOrder.name.replace(/\//g, ' ')} - Basket Items ${rangeLabel}`
              );
              setExportButtonLoading(false);
              return true;
            },
            (e) => {
              console.log('error', e);
              setExportButtonLoading(false);
              return false;
            }
          );
        },
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateChange = (dates) => {
    if (dates && dates.length === 2) {
      // The URL is the source of truth — the loader re-resolves the range from
      // it, so there is no local copy left to drift out of sync.
      queryParams.set('startDate', dates[0].format('YYYY-MM-DD'));
      queryParams.set('endDate', dates[1].format('YYYY-MM-DD'));
      queryParams.set('page', '1'); // Reset to page 1 on date change
      history.replace({
        pathname: location.pathname,
        search: queryParams.toString(),
      });
      // Reset expanded rows when date changes
      setExpandedRowKeys([]);
    }
  };

  const handlePageChange = (page, newPageSize) => {
    // When page size changes, snap back to page 1 so we don't land past the
    // new last page. Otherwise just persist the page index. Always persist
    // the size so the loader's URL-driven `pageSize` reflects the dropdown.
    const sizeChanged = newPageSize && newPageSize !== pageSize;
    queryParams.set('page', sizeChanged ? '1' : page.toString());
    if (newPageSize) {
      queryParams.set('pageSize', newPageSize.toString());
    }
    history.replace({
      pathname: location.pathname,
      search: queryParams.toString(),
    });
    // Reset expanded rows when page changes
    setExpandedRowKeys([]);
  };

  const handleExpand = (expanded, record) => {
    const keys = expanded
      ? [...expandedRowKeys, record.id]
      : expandedRowKeys.filter((key) => key !== record.id);
    setExpandedRowKeys(keys);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = (value) => {
    queryParams.set('page', '1'); // Reset to page 1 on search
    if (value) {
      queryParams.set('searchTerm', value);
    } else {
      queryParams.delete('searchTerm');
    }
    history.replace({
      pathname: location.pathname,
      search: queryParams.toString(),
    });
    // Reset expanded rows when searching
    setExpandedRowKeys([]);
  };

  const handleBasketItemsSearchChange = (values) => {
    setBasketItemsSearchTerms(values);
  };

  return React.cloneElement(props.children, {
    campaignOrder,
    pageSize,
    basketItemsData: filteredBasketItems,
    basketItemsGroupedOrderId,
    basketItemsGroupedTotalCount,
    currentPage,
    basketItemsDataLoading,
    basketItemsByDate: filteredBasketItemsByDate,
    detailBarSummary,
    dateRange,
    defaultDateRange,
    loading: basketItemsDataLoading,
    onDateChange: handleDateChange,
    onPageChange: handlePageChange,
    expandedRowKeys,
    onExpand: handleExpand,
    searchTerm,
    onSearchChange: handleSearchChange,
    onSearch: handleSearch,
    basketItemsSearchTerms,
    onBasketItemsSearchChange: handleBasketItemsSearchChange,
  });
};

export default BasketItemsTabController;
